import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Header } from "@/components/Header";
import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Check, Sparkles, Zap, Building2, ArrowRight, Coins, Globe, Briefcase, Store, Gift } from "lucide-react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

type Currency = "INR" | "USD";

interface PlanPricing {
  INR: string;
  USD: string;
}

// Merchant (Shopify/WooCommerce) plans
const merchantPlans = [
  {
    name: "Trial",
    description: "Perfect for testing the waters",
    price: { INR: "₹0", USD: "$0" } as PlanPricing,
    period: "for 30 days",
    transactionFee: "0%",
    transactionLabel: "per transaction",
    features: [
      "Full feature access",
      "Up to 10 split payments",
      "Email notifications",
      "Basic analytics",
      "Community support",
      "Extend trial on request",
    ],
    cta: "Start Free Trial",
    popular: false,
    icon: Sparkles,
  },
  {
    name: "Pay As You Go",
    description: "Only pay when you earn",
    price: { INR: "₹100", USD: "$1" } as PlanPricing,
    period: "/month",
    transactionFee: "+2%",
    transactionLabel: "of purchase value",
    features: [
      "Unlimited split payments",
      "Email notifications",
      "Automated reminders",
      "Gift voucher generation",
      "Basic analytics",
      "Email support",
    ],
    cta: "Get Started",
    popular: false,
    icon: Coins,
  },
  {
    name: "Starter",
    description: "For growing businesses",
    price: { INR: "₹499", USD: "$6" } as PlanPricing,
    period: "/month",
    transactionFee: "+1.5%",
    transactionLabel: "per transaction",
    features: [
      "Everything in Pay As You Go",
      "Up to 50 split payments/mo",
      "Priority email support",
      "Inventory reservation",
      "Advanced reminders",
      "Lower transaction fee",
    ],
    cta: "Get Started",
    popular: false,
    icon: Zap,
  },
  {
    name: "Growth",
    description: "For scaling merchants",
    price: { INR: "₹1,999", USD: "$24" } as PlanPricing,
    period: "/month",
    transactionFee: "+1%",
    transactionLabel: "per transaction",
    features: [
      "Everything in Starter",
      "Unlimited split payments",
      "Advanced analytics dashboard",
      "Priority support (24h response)",
      "Custom payment deadlines",
      "Bulk operations",
      "API access",
    ],
    cta: "Scale Up",
    popular: true,
    icon: Zap,
  },
  {
    name: "Enterprise",
    description: "For large operations",
    price: { INR: "Custom", USD: "Custom" } as PlanPricing,
    period: "pricing",
    transactionFee: "Custom",
    transactionLabel: "volume discounts",
    features: [
      "Everything in Growth",
      "White-label solution",
      "Dedicated account manager",
      "Custom integrations",
      "SLA guarantee (99.9%)",
      "On-call support",
      "Custom contracts",
    ],
    cta: "Contact Sales",
    popular: false,
    icon: Building2,
  },
];

// Professional (standalone) plans
const professionalPlans = [
  {
    name: "Free Forever",
    description: "For occasional collections",
    price: { INR: "₹0", USD: "$0" } as PlanPricing,
    period: "forever",
    transactionFee: "",
    transactionLabel: "up to 5 collections/month",
    features: [
      "5 free collections/month",
      "Email notifications",
      "Client management",
      "Basic analytics",
      "Community support",
    ],
    cta: "Get Started Free",
    popular: false,
    icon: Gift,
    isFree: true,
  },
  {
    name: "3-Month Trial",
    description: "Full access, no limits",
    price: { INR: "₹0", USD: "$0" } as PlanPricing,
    period: "for 90 days",
    transactionFee: "",
    transactionLabel: "unlimited collections during trial",
    features: [
      "Unlimited collections",
      "All premium features",
      "Priority email support",
      "Advanced analytics",
      "Custom branding",
    ],
    cta: "Start Free Trial",
    popular: false,
    icon: Sparkles,
  },
  {
    name: "Pay Per Use",
    description: "Only pay when you collect",
    price: { INR: "₹10", USD: "$0.12" } as PlanPricing,
    period: "/collection",
    transactionFee: "",
    transactionLabel: "flat fee per payment collected",
    features: [
      "No monthly commitment",
      "Unlimited collections",
      "Email notifications",
      "Automated reminders",
      "Payment tracking",
      "Email support",
    ],
    cta: "Get Started",
    popular: false,
    icon: Coins,
    isNew: true,
  },
  {
    name: "Starter",
    description: "For regular collections",
    price: { INR: "₹500", USD: "$6" } as PlanPricing,
    annualPrice: { INR: "₹400", USD: "$5" } as PlanPricing,
    period: "/month",
    transactionFee: "",
    transactionLabel: "60 collections included",
    overageLabel: "₹10 / $0.12 per extra collection",
    features: [
      "60 collections/month",
      "All features included",
      "Priority email support",
      "Client management",
      "Analytics dashboard",
      "Payment reports",
    ],
    cta: "Get Started",
    popular: true,
    icon: Zap,
  },
  {
    name: "Growth",
    description: "For high-volume professionals",
    price: { INR: "₹1,000", USD: "$12" } as PlanPricing,
    annualPrice: { INR: "₹800", USD: "$10" } as PlanPricing,
    period: "/month",
    transactionFee: "",
    transactionLabel: "120 collections included",
    overageLabel: "₹8 / $0.10 per extra collection",
    features: [
      "120 collections/month",
      "Everything in Starter",
      "Priority support (24h)",
      "Advanced analytics",
      "Bulk operations",
      "Custom branding",
    ],
    cta: "Scale Up",
    popular: false,
    icon: Zap,
  },
];

// Keep backward compatibility
const plans = merchantPlans;

// Merchant comparison features
const merchantComparisonFeatures = [
  { name: "Monthly fee", trial: "₹0", payg: "₹100 / $1", starter: "₹499 / $6", growth: "₹1,999 / $24", enterprise: "Custom" },
  { name: "Split payments per month", trial: "10", payg: "Unlimited", starter: "50", growth: "Unlimited", enterprise: "Unlimited" },
  { name: "Transaction fee", trial: "0%", payg: "2%", starter: "1.5%", growth: "1%", enterprise: "Custom" },
  { name: "Email notifications", trial: true, payg: true, starter: true, growth: true, enterprise: true },
  { name: "Automated reminders", trial: false, payg: true, starter: true, growth: true, enterprise: true },
  { name: "Inventory reservation", trial: false, payg: false, starter: true, growth: true, enterprise: true },
  { name: "Gift vouchers", trial: false, payg: true, starter: true, growth: true, enterprise: true },
  { name: "Analytics dashboard", trial: "Basic", payg: "Basic", starter: "Basic", growth: "Advanced", enterprise: "Advanced" },
  { name: "API access", trial: false, payg: false, starter: false, growth: true, enterprise: true },
  { name: "White-label", trial: false, payg: false, starter: false, growth: false, enterprise: true },
  { name: "Dedicated support", trial: false, payg: false, starter: false, growth: false, enterprise: true },
  { name: "SLA guarantee", trial: false, payg: false, starter: false, growth: false, enterprise: true },
];

// Professional comparison features
const professionalComparisonFeatures = [
  { name: "Free collections", free: "5/month", trial: "Unlimited", payPerUse: "—", starter: "—", growth: "—" },
  { name: "Trial period", free: "—", trial: "90 days", payPerUse: "—", starter: "—", growth: "—" },
  { name: "Monthly fee", free: "₹0 forever", trial: "₹0", payPerUse: "₹0", starter: "₹500 / $6", growth: "₹1,000 / $12" },
  { name: "Annual fee (20% off)", free: "—", trial: "—", payPerUse: "—", starter: "₹4,800 / $60", growth: "₹9,600 / $120" },
  { name: "Collections per month", free: "5", trial: "Unlimited", payPerUse: "Unlimited", starter: "60", growth: "120" },
  { name: "Per-collection fee", free: "₹0", trial: "₹0", payPerUse: "₹10 / $0.12", starter: "₹0", growth: "₹0" },
  { name: "Overage fee", free: "Upgrade required", trial: "—", payPerUse: "—", starter: "₹10 / $0.12", growth: "₹8 / $0.10" },
  { name: "Email notifications", free: true, trial: true, payPerUse: true, starter: true, growth: true },
  { name: "Automated reminders", free: false, trial: true, payPerUse: true, starter: true, growth: true },
  { name: "Client management", free: true, trial: true, payPerUse: true, starter: true, growth: true },
  { name: "Collection tracking", free: true, trial: true, payPerUse: true, starter: true, growth: true },
  { name: "Analytics dashboard", free: "Basic", trial: "Advanced", payPerUse: "Basic", starter: "Full", growth: "Advanced" },
  { name: "Priority support", free: false, trial: true, payPerUse: false, starter: true, growth: true },
  { name: "Custom branding", free: false, trial: true, payPerUse: false, starter: false, growth: true },
  { name: "Bulk operations", free: false, trial: true, payPerUse: false, starter: false, growth: true },
];

// Keep backward compatibility
const comparisonFeatures = merchantComparisonFeatures;

type PricingType = "merchant" | "professional";

// Reusable Pricing Card Component
interface PricingCardProps {
  plan: {
    name: string;
    description: string;
    price: PlanPricing;
    annualPrice?: PlanPricing;
    period: string;
    transactionFee: string;
    transactionLabel: string;
    overageLabel?: string;
    features: string[];
    cta: string;
    popular?: boolean;
    icon: React.ComponentType<{ className?: string }>;
    isNew?: boolean;
    isFree?: boolean;
  };
  currency: Currency;
  linkTo: string;
  isProfessional?: boolean;
  billingPeriod?: "monthly" | "annual";
}

const PricingCard = ({ plan, currency, linkTo, isProfessional, billingPeriod = "monthly" }: PricingCardProps) => {
  const showAnnual = billingPeriod === "annual" && plan.annualPrice;
  const displayPrice = showAnnual ? plan.annualPrice![currency] : plan.price[currency];
  
  return (
    <Card 
      className={cn(
        "relative flex flex-col border-2 transition-all hover:shadow-lg",
        plan.popular 
          ? isProfessional 
            ? "border-cyan-500 shadow-lg" 
            : "border-primary shadow-lg" 
          : plan.isFree
            ? "border-cyan-400/50 bg-cyan-500/5"
            : "border-border hover:border-primary/50"
      )}
    >
      {plan.popular && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span className={cn(
            "text-xs font-semibold px-3 py-1 rounded-full whitespace-nowrap",
            isProfessional 
              ? "bg-cyan-600 text-white" 
              : "bg-primary text-primary-foreground"
          )}>
            Most Popular
          </span>
        </div>
      )}
      {plan.isNew && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span className="bg-emerald-500 text-white text-xs font-semibold px-3 py-1 rounded-full">
            New
          </span>
        </div>
      )}
      {plan.isFree && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span className="bg-cyan-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
            Forever Free
          </span>
        </div>
      )}
      <CardHeader className="text-center pb-2 pt-5">
        <div className={cn(
          "w-10 h-10 rounded-lg flex items-center justify-center mx-auto mb-2",
          plan.popular 
            ? isProfessional 
              ? "bg-cyan-600 text-white" 
              : "bg-primary text-primary-foreground" 
            : plan.isFree
              ? "bg-cyan-600 text-white"
              : isProfessional 
                ? "bg-cyan-500/10 text-cyan-600" 
                : "bg-primary/10 text-primary"
        )}>
          <plan.icon className="w-5 h-5" />
        </div>
        <CardTitle className="text-lg">{plan.name}</CardTitle>
        <CardDescription className="text-xs">{plan.description}</CardDescription>
      </CardHeader>
      <CardContent className="flex-1 flex flex-col pt-2 pb-4">
        <div className="text-center mb-3">
          <div className="flex items-baseline justify-center gap-1">
            <span className="text-2xl font-bold text-foreground">
              {displayPrice}
            </span>
            <span className="text-sm text-muted-foreground">
              {showAnnual ? "/mo (billed annually)" : plan.period}
            </span>
          </div>
          {showAnnual && (
            <div className="mt-1 text-xs text-cyan-600 font-medium">
              Save 20% vs monthly
            </div>
          )}
          {plan.transactionFee && (
            <div className="mt-1 text-xs">
              <span className={cn(
                "font-semibold",
                isProfessional ? "text-cyan-600" : "text-primary"
              )}>
                {plan.transactionFee}
              </span>
              <span className="text-muted-foreground"> {plan.transactionLabel}</span>
            </div>
          )}
          {!plan.transactionFee && plan.transactionLabel && (
            <div className="mt-1 text-xs text-muted-foreground">
              {plan.transactionLabel}
            </div>
          )}
          {plan.overageLabel && (
            <div className="mt-1 text-xs text-amber-600">
              Overage: {plan.overageLabel}
            </div>
          )}
        </div>
        
        <ul className="space-y-1.5 mb-4 flex-1">
          {plan.features.slice(0, 5).map((feature) => (
            <li key={feature} className="flex items-start gap-2">
              <Check className={cn(
                "w-4 h-4 flex-shrink-0 mt-0.5",
                isProfessional ? "text-cyan-600" : "text-primary"
              )} />
              <span className="text-xs text-muted-foreground">{feature}</span>
            </li>
          ))}
          {plan.features.length > 5 && (
            <li className="text-xs text-muted-foreground pl-6">
              +{plan.features.length - 5} more
            </li>
          )}
        </ul>
        
        <Button 
          asChild 
          className={cn(
            "w-full",
            (plan.popular || plan.isFree) && isProfessional && "bg-cyan-600 hover:bg-cyan-700"
          )}
          size="sm"
          variant={plan.popular || plan.isFree ? "default" : "outline"}
        >
          <Link to={linkTo}>
            {plan.cta}
            <ArrowRight className="ml-1 w-3 h-3" />
          </Link>
        </Button>
      </CardContent>
    </Card>
  );
};

// NOTE: Using function declaration instead of const arrow to prevent TDZ errors in production builds
function Pricing() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [currency, setCurrency] = useState<Currency>("INR");
  const [pricingType, setPricingType] = useState<PricingType>("merchant");
  const [billingPeriod, setBillingPeriod] = useState<"monthly" | "annual">("monthly");

  // Sync pricing type with URL parameter on mount and when URL changes
  useEffect(() => {
    const tabParam = searchParams.get("tab");
    if (tabParam === "professional") {
      setPricingType("professional");
    } else if (tabParam === "merchant" || !tabParam) {
      setPricingType("merchant");
    }
  }, [searchParams]);

  // Update URL when pricing type changes
  const handlePricingTypeChange = (type: PricingType) => {
    setPricingType(type);
    setSearchParams(type === "professional" ? { tab: "professional" } : {}, { replace: true });
  };

  const activePlans = pricingType === "merchant" ? merchantPlans : professionalPlans;

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="Pricing" 
        description="Simple, transparent pricing for Shakatah payment solutions. Choose the plan that fits your business."
        url="/pricing"
      />
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="container py-20 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
            <Sparkles className="w-4 h-4" />
            Simple, transparent pricing
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-foreground mb-6">
            Choose Your Plan
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
            Start with a free trial, then pick the plan that grows with your business. 
            No hidden fees, cancel anytime.
          </p>

          {/* Pricing Type Toggle */}
          <div className="flex flex-col items-center gap-4 mb-6">
            <Tabs value={pricingType} onValueChange={(v) => handlePricingTypeChange(v as PricingType)} className="w-fit">
              <TabsList className="grid w-full grid-cols-2 h-12">
                <TabsTrigger value="merchant" className="gap-2 px-6">
                  <Store className="w-4 h-4" />
                  <span className="hidden sm:inline">For Merchants</span>
                  <span className="sm:hidden">Merchants</span>
                </TabsTrigger>
                <TabsTrigger value="professional" className="gap-2 px-6">
                  <Briefcase className="w-4 h-4" />
                  <span className="hidden sm:inline">For Professionals</span>
                  <span className="sm:hidden">Professionals</span>
                </TabsTrigger>
              </TabsList>
            </Tabs>
            <p className="text-sm text-muted-foreground">
              {pricingType === "merchant" 
                ? "Group payments for Shopify & WooCommerce stores"
                : "Payment collections for service professionals"
              }
            </p>
          </div>

          {/* Currency & Billing Period Toggles */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            {/* Currency Toggle */}
            <div className="flex items-center gap-3 p-2 bg-muted/50 rounded-full">
              <Globe className="w-4 h-4 text-muted-foreground" />
              <Label 
                htmlFor="currency-toggle" 
                className={cn(
                  "text-sm font-medium cursor-pointer transition-colors",
                  currency === "INR" ? "text-foreground" : "text-muted-foreground"
                )}
              >
                ₹ INR
              </Label>
              <Switch
                id="currency-toggle"
                checked={currency === "USD"}
                onCheckedChange={(checked) => setCurrency(checked ? "USD" : "INR")}
              />
              <Label 
                htmlFor="currency-toggle" 
                className={cn(
                  "text-sm font-medium cursor-pointer transition-colors",
                  currency === "USD" ? "text-foreground" : "text-muted-foreground"
                )}
              >
                $ USD
              </Label>
            </div>

            {/* Annual Billing Toggle - Only for Professional */}
            {pricingType === "professional" && (
              <div className="flex items-center gap-3 p-2 bg-muted/50 rounded-full">
                <Label 
                  htmlFor="billing-toggle" 
                  className={cn(
                    "text-sm font-medium cursor-pointer transition-colors",
                    billingPeriod === "monthly" ? "text-foreground" : "text-muted-foreground"
                  )}
                >
                  Monthly
                </Label>
                <Switch
                  id="billing-toggle"
                  checked={billingPeriod === "annual"}
                  onCheckedChange={(checked) => setBillingPeriod(checked ? "annual" : "monthly")}
                />
                <Label 
                  htmlFor="billing-toggle" 
                  className={cn(
                    "text-sm font-medium cursor-pointer transition-colors flex items-center gap-1",
                    billingPeriod === "annual" ? "text-foreground" : "text-muted-foreground"
                  )}
                >
                  Annual
                  <span className="text-xs bg-cyan-600 text-white px-1.5 py-0.5 rounded-full">
                    Save 20%
                  </span>
                </Label>
              </div>
            )}
          </div>
        </section>

        {/* Pricing Cards */}
        <section className="container pb-12">
          {pricingType === "merchant" ? (
            <>
              {/* Merchant: Top row - 3 cards */}
              <div className="grid md:grid-cols-3 gap-4 mb-4">
                {activePlans.slice(0, 3).map((plan) => (
                  <PricingCard 
                    key={plan.name} 
                    plan={plan} 
                    currency={currency} 
                    linkTo="/login?type=business" 
                  />
                ))}
              </div>
              {/* Merchant: Bottom row - 2 cards centered */}
              <div className="grid md:grid-cols-2 gap-4 max-w-2xl mx-auto">
                {activePlans.slice(3).map((plan) => (
                  <PricingCard 
                    key={plan.name} 
                    plan={plan} 
                    currency={currency} 
                    linkTo="/login?type=business" 
                  />
                ))}
              </div>
            </>
          ) : (
            /* Professional: 5 cards - 3 top row, 2 bottom centered */
            <>
              <div className="grid md:grid-cols-3 gap-4 mb-4">
                {activePlans.slice(0, 3).map((plan) => (
                  <PricingCard 
                    key={plan.name} 
                    plan={plan} 
                    currency={currency} 
                    linkTo="/login?type=professional"
                    isProfessional 
                    billingPeriod={billingPeriod}
                  />
                ))}
              </div>
              <div className="grid md:grid-cols-2 gap-4 max-w-2xl mx-auto">
                {activePlans.slice(3).map((plan) => (
                  <PricingCard 
                    key={plan.name} 
                    plan={plan} 
                    currency={currency} 
                    linkTo="/login?type=professional"
                    isProfessional 
                    billingPeriod={billingPeriod}
                  />
                ))}
              </div>
            </>
          )}
        </section>

        {/* Highlight Section - Conditional */}
        <section className="container pb-20">
          {pricingType === "merchant" ? (
            <Card className="border-2 border-emerald-500/30 bg-emerald-500/5">
              <CardContent className="py-8">
                <div className="flex flex-col md:flex-row items-center gap-6">
                  <div className="p-4 bg-emerald-500/10 rounded-xl">
                    <Coins className="w-10 h-10 text-emerald-600" />
                  </div>
                  <div className="flex-1 text-center md:text-left">
                    <h3 className="text-2xl font-bold text-foreground mb-2">
                      Pay As You Go — Perfect for Starting Out
                    </h3>
                    <p className="text-muted-foreground max-w-2xl">
                      Just {currency === "INR" ? "₹100" : "$1"}/month + 2% of each purchase value. 
                      No commitment, no minimum volume. Only pay when your customers complete split payments. 
                      Ideal for merchants testing the waters or with seasonal demand.
                    </p>
                  </div>
                  <Button asChild className="bg-emerald-600 hover:bg-emerald-700">
                    <Link to="/login?type=business">
                      Start Now
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-2 border-cyan-500/30 bg-cyan-500/5">
              <CardContent className="py-8">
                <div className="flex flex-col md:flex-row items-center gap-6">
                  <div className="p-4 bg-cyan-500/10 rounded-xl">
                    <Gift className="w-10 h-10 text-cyan-600" />
                  </div>
                  <div className="flex-1 text-center md:text-left">
                    <h3 className="text-2xl font-bold text-foreground mb-2">
                      Free Forever — No Credit Card Required
                    </h3>
                    <p className="text-muted-foreground max-w-2xl">
                      Get 5 collections every month, forever. No payment details needed, no strings attached.
                      Need more? Upgrade anytime to Pay Per Use ({currency === "INR" ? "₹10" : "$0.12"}/collection) 
                      or get a monthly bundle with overage protection.
                    </p>
                  </div>
                  <Button asChild className="bg-cyan-600 hover:bg-cyan-700">
                    <Link to="/login?type=professional">
                      Get Started Free
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </section>

        {/* Feature Comparison Table */}
        <section className="container pb-24">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-foreground mb-4">Compare Plans</h2>
            <p className="text-muted-foreground">See what's included in each tier</p>
          </div>
          
          <div className="overflow-x-auto">
            {pricingType === "merchant" ? (
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-4 px-4 font-semibold text-foreground">Feature</th>
                    <th className="text-center py-4 px-4 font-semibold text-foreground">Trial</th>
                    <th className="text-center py-4 px-4 font-semibold text-foreground bg-emerald-500/5">Pay As You Go</th>
                    <th className="text-center py-4 px-4 font-semibold text-foreground">Starter</th>
                    <th className="text-center py-4 px-4 font-semibold text-foreground bg-primary/5">Growth</th>
                    <th className="text-center py-4 px-4 font-semibold text-foreground">Enterprise</th>
                  </tr>
                </thead>
                <tbody>
                  {merchantComparisonFeatures.map((feature, index) => (
                    <tr 
                      key={feature.name} 
                      className={cn(
                        "border-b border-border",
                        index % 2 === 0 ? "bg-muted/30" : ""
                      )}
                    >
                      <td className="py-4 px-4 text-sm text-foreground">{feature.name}</td>
                      <td className="py-4 px-4 text-center">
                        {typeof feature.trial === "boolean" ? (
                          feature.trial ? (
                            <Check className="w-5 h-5 text-primary mx-auto" />
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )
                        ) : (
                          <span className="text-sm text-muted-foreground">{feature.trial}</span>
                        )}
                      </td>
                      <td className="py-4 px-4 text-center bg-emerald-500/5">
                        {typeof feature.payg === "boolean" ? (
                          feature.payg ? (
                            <Check className="w-5 h-5 text-emerald-600 mx-auto" />
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )
                        ) : (
                          <span className="text-sm text-muted-foreground">{feature.payg}</span>
                        )}
                      </td>
                      <td className="py-4 px-4 text-center">
                        {typeof feature.starter === "boolean" ? (
                          feature.starter ? (
                            <Check className="w-5 h-5 text-primary mx-auto" />
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )
                        ) : (
                          <span className="text-sm text-muted-foreground">{feature.starter}</span>
                        )}
                      </td>
                      <td className="py-4 px-4 text-center bg-primary/5">
                        {typeof feature.growth === "boolean" ? (
                          feature.growth ? (
                            <Check className="w-5 h-5 text-primary mx-auto" />
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )
                        ) : (
                          <span className="text-sm font-medium text-foreground">{feature.growth}</span>
                        )}
                      </td>
                      <td className="py-4 px-4 text-center">
                        {typeof feature.enterprise === "boolean" ? (
                          feature.enterprise ? (
                            <Check className="w-5 h-5 text-primary mx-auto" />
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )
                        ) : (
                          <span className="text-sm text-muted-foreground">{feature.enterprise}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-4 px-3 font-semibold text-foreground text-sm">Feature</th>
                    <th className="text-center py-4 px-2 font-semibold text-foreground text-sm bg-cyan-500/10">Free Forever</th>
                    <th className="text-center py-4 px-2 font-semibold text-foreground text-sm">3-Month Trial</th>
                    <th className="text-center py-4 px-2 font-semibold text-foreground text-sm bg-emerald-500/5">Pay Per Use</th>
                    <th className="text-center py-4 px-2 font-semibold text-foreground text-sm bg-cyan-500/5">Starter</th>
                    <th className="text-center py-4 px-2 font-semibold text-foreground text-sm">Growth</th>
                  </tr>
                </thead>
                <tbody>
                  {professionalComparisonFeatures.map((feature, index) => (
                    <tr 
                      key={feature.name} 
                      className={cn(
                        "border-b border-border",
                        index % 2 === 0 ? "bg-muted/30" : ""
                      )}
                    >
                      <td className="py-3 px-3 text-sm text-foreground">{feature.name}</td>
                      <td className="py-3 px-2 text-center bg-cyan-500/10">
                        {typeof feature.free === "boolean" ? (
                          feature.free ? (
                            <Check className="w-5 h-5 text-cyan-600 mx-auto" />
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )
                        ) : (
                          <span className="text-sm text-muted-foreground">{feature.free}</span>
                        )}
                      </td>
                      <td className="py-3 px-2 text-center">
                        {typeof feature.trial === "boolean" ? (
                          feature.trial ? (
                            <Check className="w-5 h-5 text-cyan-600 mx-auto" />
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )
                        ) : (
                          <span className="text-sm text-muted-foreground">{feature.trial}</span>
                        )}
                      </td>
                      <td className="py-3 px-2 text-center bg-emerald-500/5">
                        {typeof feature.payPerUse === "boolean" ? (
                          feature.payPerUse ? (
                            <Check className="w-5 h-5 text-emerald-600 mx-auto" />
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )
                        ) : (
                          <span className="text-sm text-muted-foreground">{feature.payPerUse}</span>
                        )}
                      </td>
                      <td className="py-3 px-2 text-center bg-cyan-500/5">
                        {typeof feature.starter === "boolean" ? (
                          feature.starter ? (
                            <Check className="w-5 h-5 text-cyan-600 mx-auto" />
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )
                        ) : (
                          <span className="text-sm font-medium text-foreground">{feature.starter}</span>
                        )}
                      </td>
                      <td className="py-3 px-2 text-center">
                        {typeof feature.growth === "boolean" ? (
                          feature.growth ? (
                            <Check className="w-5 h-5 text-cyan-600 mx-auto" />
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )
                        ) : (
                          <span className="text-sm text-muted-foreground">{feature.growth}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>

        {/* FAQ CTA */}
        <section className="container pb-24">
          <div className="bg-gradient-to-br from-primary/10 to-accent/10 rounded-3xl p-12 text-center">
            <h2 className="text-3xl font-bold text-foreground mb-4">
              Still have questions?
            </h2>
            <p className="text-lg text-muted-foreground mb-8 max-w-xl mx-auto">
              Our team is here to help you choose the right plan for your business.
            </p>
            <Button asChild size="lg">
              <Link to="/contact">
                Contact Us
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </Button>
          </div>
        </section>
      </main>
      
      <footer className="border-t border-border bg-card">
        <div className="container py-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2">
              <Gift className="w-4 h-4 text-primary" />
              <span className="font-medium text-foreground">Shakatah</span>
            </div>
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <Link to="/" className="hover:text-foreground transition-colors">Home</Link>
              <Link to="/terms-of-service" className="hover:text-foreground transition-colors">Terms</Link>
              <Link to="/privacy-policy" className="hover:text-foreground transition-colors">Privacy</Link>
              <Link to="/refund-policy" className="hover:text-foreground transition-colors">Refunds</Link>
              <a href="/#faq" className="hover:text-foreground transition-colors">FAQ</a>
              <Link to="/contact" className="hover:text-foreground transition-colors">Contact</Link>
            </div>
          </div>
          <div className="mt-4 text-center text-xs text-muted-foreground">
            © {new Date().getFullYear()} Shakatah. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Pricing;
